import { DataRowInfo, ObjectSpaceService } from '@barthauer/abc/core/data';
import { forkJoin, from, Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { LAYER_PRINT_BOX } from './map.component';
import { MapLoader } from './map.loader';
import { MapQueryCache } from './map.query-cache';
import { MapView } from './map.view';
import { DataEntity } from './models/element.model';
import { MapLayer } from './models/map.model';
import { MapService } from './services/map.service';

declare let ol: any;

export class MapSelection {
  private NO_BASYS_OBJECTS_FOUND = 'Kein BaSYS-Objekt gefunden!';
  private selectinteraction: any;
  private selectedFeatures: any;

  constructor(
    private olmap: any,
    private mapLoader: MapLoader,
    private mapView: MapView,
    private mapService: MapService,
    private objectSpaceService: ObjectSpaceService
  ) {
    this.mapLoader.mapSelection = this;
    this.selectinteraction = new ol.interaction.Select({
      filter: function (feature, layer) {
        return !!layer && layer.get('name') !== LAYER_PRINT_BOX;
      },
      style: null,
      condition: () => false,
    });
    this.olmap.addInteraction(this.selectinteraction);
    this.selectedFeatures = this.selectinteraction.getFeatures();
    this.selectedFeatures.on('add', (e) => {
      this.mapView.refreshDelayed();
    });
    this.selectedFeatures.on('remove', (e) => {
      this.mapView.refreshDelayed();
    });

    const dragBox = new ol.interaction.DragBox({
      condition: ol.events.condition.platformModifierKeyOnly,
      style: new ol.style.Style({
        stroke: new ol.style.Stroke({
          color: [0, 0, 255, 1],
        }),
      }),
    });
    this.olmap.addInteraction(dragBox);
  }

  public getMap(): any {
    return this.olmap;
  }

  public getMapQueryCache(): MapQueryCache {
    return this.mapLoader?.getQueryCache();
  }

  public getAllMapLayerDefinitions(): MapLayer[] {
    const result: MapLayer[] = [];
    const arr = this.mapLoader?.getAllLayers();
    if (arr) {
      for (const ly of arr) {
        const def = ly?.layerdefinition;
        if (def) {
          result.push(def);
        }
      }
    }
    return result;
  }

  public addSelectedFeature(feature: any): void {
    const orig = this.findSelectedFeature(feature);
    if (orig) {
      this.selectedFeatures.remove(orig);
    }

    if (orig != feature) {
      this.selectedFeatures.push(feature);
      feature?.changed();
    }
  }

  public toggleSelectedFeature(feature: any): void {
    const orig = this.findSelectedFeature(feature);
    if (orig) {
      this.selectedFeatures.remove(orig);
    } else {
      this.selectedFeatures.push(feature);
    }
    feature?.changed();
  }

  public getSelectedFeatures(): any {
    return this.selectedFeatures;
  }

  private findSelectedFeature(feature: any): any {
    const arr: any[] = this.selectedFeatures.getArray();
    if (!arr?.length) {
      return false;
    }

    const id = feature?.getId();
    return arr?.find((_) => id == _.getId());
  }

  public isSelectedFeature(feature: any): boolean {
    return this.findSelectedFeature(feature);
  }

  public clearSelectedFeatures(): void {
    this.clearSelectedFeaturesCore(true);
  }

  private clearSelectedFeaturesCore(refreshView: boolean): void {
    const arr: any[] = [];
    for (const f of this.selectedFeatures.getArray()) {
      arr.push(f);
    }
    this.clearFeatures(this.selectedFeatures);
    for (let f of arr) {
      try {
        f?.changed();
      } catch (e) {
        console.error(e);
      }
    }
    if (refreshView) {
      this.mapView.refresh();
    }
  }

  private clearFeatures(features: any): void {
    if (features) features.clear();
  }

  public moveToObjects(objectInfos: DataEntity[], addNotLoadedFeatures: boolean = true): void {
    this.moveToObjectsCore(objectInfos, addNotLoadedFeatures, true, true);
  }

  public moveToObjectsWithoutZoom(objectInfos: DataEntity[], addNotLoadedFeatures: boolean = true): void {
    this.moveToObjectsCore(objectInfos, addNotLoadedFeatures, true, false);
  }

  private moveToObjectsCore(
    objectInfos: DataEntity[],
    addNotLoadedFeatures: boolean,
    select: boolean,
    zoom: boolean
  ): void {
    if (this.olmap !== null && this.olmap.getLayers().getLength() > 0) {
      if (select) {
        this.clearSelectedFeaturesCore(!objectInfos?.length);
      }
      const allVLayers = this.mapLoader.getVectorLayers();
      const reloadLayers = [];
      let allRelevantLayerLoaded = true;

      const getBoxes: Observable<any>[] = [];
      for (const l of allVLayers) {
        const src = l.getSource();
        getBoxes.push(
          from(this.checkIfRelevant(src, objectInfos)).pipe(
            map((isRelevant) => {
              if (isRelevant) {
                reloadLayers.push(l);
                if (src.getFeatures().length === 0) allRelevantLayerLoaded = false;
                else return this.calcExtentBySrc(src, objectInfos, select);
              }
            })
          )
        );
      }

      forkJoin(getBoxes).subscribe((boxes) => {
        let finalExt = ol.extent.createEmpty();
        for (const ext of boxes) {
          if (ext !== undefined && !ol.extent.isEmpty(ext)) {
            if (ol.extent.isEmpty(finalExt)) finalExt = ext;
            else finalExt = ol.extent.extend(ext, finalExt);
          }
        }
        if (!ol.extent.isEmpty(finalExt) && zoom) this.mapView.fitToObjects(finalExt, objectInfos?.length);
        else if (ol.extent.isEmpty(finalExt)) allRelevantLayerLoaded = false; //to ensure single object to be loaded from db

        if (!allRelevantLayerLoaded && addNotLoadedFeatures)
          this.zoomToObjectsByLayersParallel(objectInfos, reloadLayers, select, zoom);
        else this.tryClearSelection(objectInfos);
      });
    }
  }

  public moveToDataRows(rows: DataRowInfo[]): void {
    this.moveToDataRowsCore(rows, true, true);
  }

  private moveToDataRowsCore(rows: DataRowInfo[], select: boolean, zoom: boolean): void {
    if (this.olmap !== null && this.olmap.getLayers().getLength() > 0) {
      if (select) {
        this.clearSelectedFeaturesCore(!rows?.length);
      }

      const allVLayers = this.mapLoader.getVectorLayers();

      const getBoxes: Observable<any>[] = [];
      for (const l of allVLayers) {
        const src = l.getSource();

        getBoxes.push(
          of(this.findRelevantDataRows(src, rows)).pipe(
            concatMap((resultRows) => {
              if (resultRows?.length) {
                return this.calcDataRowsExtentBySrc(src, resultRows, select);
              } else {
                return of(null);
              }
            })
          )
        );
      }

      forkJoin(getBoxes).subscribe((boxes) => {
        let finalExt = ol.extent.createEmpty();
        for (const ext of boxes) {
          if (ext && !ol.extent.isEmpty(ext)) {
            if (ol.extent.isEmpty(finalExt)) finalExt = ext;
            else finalExt = ol.extent.extend(ext, finalExt);
          }
        }
        if (!ol.extent.isEmpty(finalExt) && zoom) this.mapView.fitToObjects(finalExt, rows?.length);
      });
    }
  }

  private tryClearSelection(objectInfos: DataEntity[]) {
    if (false && objectInfos?.length === 1) {
      setTimeout(() => {
        this.clearSelectedFeatures();
      }, 3000);
    }
  }

  private calcExtentBySrc(src: any, objectInfos: DataEntity[], select: boolean): any {
    let ext = ol.extent.createEmpty();

    for (const f of src.getFeatures()) {
      const oId = MapLoader.getFeatureObjectId(f);
      if (oId !== undefined && objectInfos.findIndex((x) => x.id === oId) > -1) {
        const geom = f.getGeometry();
        if (geom !== undefined) {
          const curExt = geom.getExtent();
          if (select) this.addSelectedFeature(f);
          if (ol.extent.isEmpty(ext)) ext = curExt;
          else ext = ol.extent.extend(ext, f.getGeometry().getExtent());
        }
      }
    }

    return ext;
  }

  private calcDataRowsExtentBySrc(
    src: any,
    rows: DataRowInfo[],
    select: boolean,
    tryLoadData: boolean = true
  ): Observable<any> {
    let ext = ol.extent.createEmpty();
    const obj = {};

    for (const f of src.getFeatures()) {
      const oId: string = f.getId()?.toString();
      if (oId !== undefined && rows.findIndex((x) => x.Id == oId) > -1) {
        const geom = f.getGeometry();
        if (geom !== undefined) {
          const curExt = geom.getExtent();
          if (select) this.addSelectedFeature(f);
          if (ol.extent.isEmpty(ext)) ext = curExt;
          else ext = ol.extent.extend(ext, f.getGeometry().getExtent());
          obj[oId] = true;
        }
      }
    }
    if (tryLoadData && Object.keys(obj).length < rows.length) {
      src.clear();
      const load: Observable<void> = src.loader(null, 1, this.mapView.featureProjection);
      return load.pipe(
        concatMap(() => {
          return this.calcDataRowsExtentBySrc(src, rows, select, false);
        })
      );
    }

    return of(ext);
  }

  private zoomToObjectsByLayersParallel(
    objectInfos: DataEntity[],
    reloadLayers: any[],
    select: boolean,
    zoom: boolean
  ) {
    if (reloadLayers !== undefined && reloadLayers.length > 0) {
      const objQueries: Observable<any>[] = [];
      for (const l of reloadLayers) {
        const q$ = this.mapLoader.getObjectSpaceFeatures(
          l,
          objectInfos,
          this.mapView.dataProjection,
          this.mapView.featureProjection
        );
        objQueries.push(q$);
      }

      forkJoin(objQueries).subscribe(
        (results) => {
          let finalExtent = ol.extent.createEmpty();

          for (const features of results) {
            if (features) {
              if (select) {
                for (const f of features) {
                  this.addSelectedFeature(f);
                }
              }
              const ext = this.calcExtent(features);
              if (ext !== undefined && !ol.extent.isEmpty(ext)) {
                if (ol.extent.isEmpty(finalExtent)) finalExtent = ext;
                else finalExtent = ol.extent.extend(ext, finalExtent);
              }
            }
          }

          const success = !ol.extent.isEmpty(finalExtent);
          if (success && zoom) this.mapView.fitToObjects(finalExtent, objectInfos?.length);

          this.tryClearSelection(objectInfos);

          if (!success) this.mapService.sendMapShortMessage(this.NO_BASYS_OBJECTS_FOUND);
        },

        (error) => {
          this.mapService.sendMapShortMessage(error);
        }
      );
    }
  }

  private calcExtent(features: any): any {
    let ext = ol.extent.createEmpty();

    for (const f of features) {
      const geom = f.getGeometry();
      if (geom !== undefined) {
        const curExt = geom.getExtent();
        if (ext === undefined || ol.extent.isEmpty(ext)) ext = curExt;
        else ext = ol.extent.extend(ext, curExt);
      }
    }

    return ext;
  }

  public static toDataEntity(feature: any): DataEntity {
    return MapLoader.toDataEntity(feature);
  }

  public refreshLayerSources(objectInfos: DataEntity[]) {
    if (objectInfos === undefined || objectInfos.length === 0) {
      this.refreshAllLayerSources();
    } else {
      this.refreshLayerSourceByObjectInfos(objectInfos).then(() => {});
    }
  }

  private async refreshLayerSourceByObjectInfos(objectInfos: DataEntity[]) {
    if (this.olmap !== null && this.olmap.getLayers().getLength() > 0) {
      this.mapLoader.setDataEntitiesToRefresh(objectInfos);
      const allVLayers = this.mapLoader.getVectorLayers();
      for (const l of allVLayers) {
        const src = l.getSource();

        if (src !== undefined && (await this.checkIfRelevant(src, objectInfos))) {
          src.refresh();
        }
      }
    }
  }

  private async checkIfRelevant(src: any, objectInfos: DataEntity[]): Promise<boolean> {
    if (src !== undefined && objectInfos !== undefined && objectInfos.length > 0) {
      const modelLayer: MapLayer = src.layerdefinition;
      if (modelLayer !== undefined) {
        const objTypeName: string = modelLayer.query?.ObjectQueries[0].ObjectType;
        for (const info of objectInfos) {
          let isRelevant = await this.objectSpaceService.is(info.objectType, objTypeName);
          if (!isRelevant) {
            isRelevant = await this.objectSpaceService.is(objTypeName, info.objectType);
          }
          if (isRelevant) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private findRelevantDataRows(src: any, rows: DataRowInfo[]): DataRowInfo[] {
    const result: DataRowInfo[] = [];
    if (src !== undefined && rows !== undefined && rows.length > 0) {
      const modelLayer: MapLayer = src.layerdefinition;
      if (modelLayer !== undefined) {
        for (const row of rows) {
          if (modelLayer.fullName === row.DataSetName) {
            result.push(row);
          }
        }
      }
    }
    return result;
  }

  private refreshAllLayerSources() {
    if (this.olmap !== null && this.olmap.getLayers().getLength() > 0) {
      const allVLayers = this.mapLoader.getVectorLayers();
      for (const l of allVLayers) {
        const src = l.getSource();

        if (src !== undefined) src.refresh();
      }
    }
  }

  public canZoomToLayer(layer: any): boolean {
    return this.mapLoader.canReadLayerExtent(layer);
  }

  public zoomToLayer(layer: any): void {
    this.readLayerExtent(layer)?.subscribe((rect) => {
      if (rect) {
        this.mapView.fit(rect);
      }
    });
  }

  private readLayerExtent(layer: any): Observable<[number, number, number, number]> {
    if (this.mapLoader.canReadLayerExtent(layer)) {
      return this.mapLoader.readLayerExtent(layer);
    }
    return null;
  }

  public canOpenLayerDataTable(layer: any): boolean {
    if (this.mapLoader.getObjectTypeFromLayer(layer)) {
      return true;
    }
    return false;
  }

  public openLayerDataTable(layer: any): void {
    const objectType = this.mapLoader.getObjectTypeFromLayer(layer);
    if (objectType) {
      Error('openLayerDataTable');
    }
  }

  public getMapImage(size: number[], action: (url: string) => void): void {
    return this.mapView.getCurrentExtentMapImage(this.mapLoader, size, action);
  }

  public isOfType(objectType: string, testObjectType: string): Promise<boolean> {
    return this.objectSpaceService.is(objectType, testObjectType);
  }
}
